import { CLAIM_LINK_BOOTSTRAP } from "~/utils/claim-link";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  LinksFunction,
  LoaderFunctionArgs,
  data,
  useRouteLoaderData,
} from "react-router";
import styles from "./tailwind.css?url";
import fontAwesomeStyles from "@fortawesome/fontawesome-svg-core/styles.css?url";
import { gql } from "~/__generated__/graphql";
import { createApolloClient } from "~/.server/apollo";
import { PosthogProvider } from "~/context/posthog";
import { GetUserQuery } from "~/__generated__/graphql/graphql";
import { config } from "@fortawesome/fontawesome-svg-core";
import { useSentryUser } from "~/hooks/use-sentry-user";
import { ErrorBoundary as SentryErrorBoundary } from "~/components/ErrorBoundary";
import { getUTMParamsWithSession } from "~/.server/appsflyer";
import { commitSession } from "~/.server/session";
import { getPosthogBootstrap, type PosthogBootstrap } from "~/.server/posthog";
import {
  resolveLocaleFromAcceptLanguage,
  type SupportedLocale,
} from "~/utils/localization";

config.autoAddCss = false;

export const links: LinksFunction = () => [
  // Preconnect to external services for performance
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  { rel: "dns-prefetch", href: "https://firebaseapp.com" },
  { rel: "dns-prefetch", href: "https://app.posthog.com" },
  // Favicons
  {
    rel: "icon",
    href: "/favicon.ico",
    type: "image/x-icon",
  },
  {
    rel: "icon",
    href: "/favicon-32x32.png",
    type: "image/png",
    sizes: "32x32",
  },
  {
    rel: "apple-touch-icon",
    href: "/apple-touch-icon.png",
    sizes: "180x180",
  },
  {
    rel: "manifest",
    href: "/site.webmanifest",
  },
  { rel: "stylesheet", href: fontAwesomeStyles },
  { rel: "stylesheet", href: styles },
];

const COUNTRY_HEADER_NAMES = [
  "cf-ipcountry",
  "x-vercel-ip-country",
  "cloudfront-viewer-country",
];

function resolveVisitorCountryCode(request: Request): string | undefined {
  for (const headerName of COUNTRY_HEADER_NAMES) {
    const countryCode = request.headers.get(headerName)?.trim().toUpperCase();
    if (countryCode && countryCode !== "XX" && /^[A-Z]{2}$/.test(countryCode)) {
      return countryCode;
    }
  }

  // Language is only a fallback; let the client try its timezone first.
  return undefined;
}

const USER_QUERY = gql(`
  query GetUser {
    user {
      id
      authID
      email
      contactEmail
      name
      firstName
      lastName
      profileImageUrl
      age
      gender
      bio
      headline
      phoneNumber
      instagram
      anonymous
      isAppUser
    }
  }
`);

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = resolveLocaleFromAcceptLanguage(
    request.headers.get("Accept-Language"),
  );
  const countryCode = resolveVisitorCountryCode(request);
  const ENV = {
    FIREBASE_API_KEY: process.env.FIREBASE_API_KEY,
    FIREBASE_AUTH_DOMAIN: process.env.FIREBASE_AUTH_DOMAIN,
    FIREBASE_PROJECT_ID: process.env.FIREBASE_PROJECT_ID,
    FIREBASE_APP_ID: process.env.FIREBASE_APP_ID,
    POSTHOG_API_KEY: process.env.POSTHOG_API_KEY,
    GOOGLE_MAPS_API_KEY: process.env.GOOGLE_MAPS_API_KEY,
    SENTRY_DSN: process.env.SENTRY_DSN,
    SENTRY_ENVIRONMENT: process.env.SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    PRELUDE_SDK_KEY: process.env.PRELUDE_SDK_KEY,
    TICKET_OUTCOME_SMS_ENABLED:
      process.env.TICKET_OUTCOME_SMS_ENABLED?.trim().toLowerCase() === "true",
  };

  // Capture UTM params on first landing (first-touch attribution)
  const { session, needsCommit } = await getUTMParamsWithSession(request);

  const { client, authenticated } = await createApolloClient(request);

  let user: GetUserQuery["user"];

  if (authenticated) {
    try {
      const { data: userResult } = await client.query({
        query: USER_QUERY,
      });

      if (userResult?.user) {
        user = userResult.user;
      }
    } catch (error) {
      console.debug("An error occurred getting authenticated user");
    }
  }

  // A failed profile read does not imply logout. Resolve the UID independently
  // from the signed-in session when the API cannot supply it.
  let posthogAuthID = authenticated ? user?.authID ?? undefined : null;
  if (authenticated && !posthogAuthID) {
    const authToken = session.get("auth");
    if (authToken) {
      try {
        const { serverAuth } = await import("~/.server/firebase");
        posthogAuthID = (await serverAuth.verifySessionCookie(authToken)).uid;
      } catch {
        console.debug("Unable to verify analytics session identity");
      }
    }
  }

  // Unknown identity must not rotate the cookie or reset an identified client.
  const { bootstrap: posthogBootstrap, setCookieHeader: posthogSetCookie } =
    posthogAuthID === undefined ? {} : await getPosthogBootstrap(request, posthogAuthID);

  const headers = new Headers();
  if (needsCommit) {
    headers.append("Set-Cookie", await commitSession(session));
  }
  if (posthogSetCookie) {
    headers.append("Set-Cookie", posthogSetCookie);
  }

  return data(
    { ENV, authenticated, user, posthogBootstrap, locale, countryCode },
    { headers },
  );
};

// Define the actual data shape for useMatches() access
// (data() wraps the return type, but useMatches().data gives us the unwrapped value)
export type RootLoaderType = {
  ENV: {
    FIREBASE_API_KEY: string | undefined;
    FIREBASE_AUTH_DOMAIN: string | undefined;
    FIREBASE_PROJECT_ID: string | undefined;
    FIREBASE_APP_ID: string | undefined;
    POSTHOG_API_KEY: string | undefined;
    GOOGLE_MAPS_API_KEY: string | undefined;
    SENTRY_DSN: string | undefined;
    SENTRY_ENVIRONMENT: string;
    PRELUDE_SDK_KEY: string | undefined;
    TICKET_OUTCOME_SMS_ENABLED: boolean;
  };
  authenticated: boolean;
  user: GetUserQuery["user"];
  posthogBootstrap: PosthogBootstrap | undefined;
  locale: SupportedLocale;
  countryCode: string | undefined;
};

export function useRootLocale(): SupportedLocale {
  const data = useRouteLoaderData("root") as RootLoaderType | undefined;
  return data?.locale ?? "en-AU";
}

export function useRootCountryCode(): string | undefined {
  const data = useRouteLoaderData("root") as RootLoaderType | undefined;
  return data?.countryCode;
}

export function useRootEnvironment(): RootLoaderType["ENV"] | undefined {
  const data = useRouteLoaderData("root") as RootLoaderType | undefined;
  return data?.ENV;
}

export function useRootPosthogBootstrap(): PosthogBootstrap | undefined {
  const data = useRouteLoaderData("root") as RootLoaderType | undefined;
  return data?.posthogBootstrap;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const locale = useRootLocale();

  return (
    <html lang={locale}>
      <head>
        <meta charSet="utf-8" />
        <script dangerouslySetInnerHTML={{ __html: CLAIM_LINK_BOOTSTRAP }} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <script src="/appsflyer.js" />
        <script src="/appsflyer-smart-banner.js" />
      </head>
      <body>
        <LayoutContent>{children}</LayoutContent>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function LayoutContent({ children }: { children: React.ReactNode }) {
  const bootstrap = useRootPosthogBootstrap();
  return <PosthogProvider bootstrap={bootstrap}>{children}</PosthogProvider>;
}

export { SentryErrorBoundary as ErrorBoundary };

export default function App() {
  useSentryUser();
  return <Outlet />;
}
